import React, { Component } from 'react';

import { Uppy, Meta } from '@uppy/core';
import DragDrop from '@uppy/react/lib/DragDrop.js';
import XHRUpload from '@uppy/xhr-upload';
import cn from 'classnames';

import DeleteIcon from './assets/delete.svg?react';
import FileIcon from './assets/file.svg?react';
import { UploadResponse } from '../../../../types/uploadResponse';
import { roundNumber } from '../../../../utils';
import { Nullable } from '../../../../utils/types';

import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';

type Props = {
  url: string;
  fieldName: string;
  multiple: boolean;
  fileTypes: string[];
  sizeView: 'kb' | 'mb';
  maxFileSizeMb?: number;
  uploadedFileName?: string | string[];
  dropHereOr?: string;
  disabled?: boolean;
  onUploaded: (fieldName: string, fileName: string | string[]) => void;
  onRemove: (fileName: string) => void;
};

type FileInfo = {
  id?: string;
  fileId: Nullable<number>;
  name: string;
  displayName: string;
  size: number;
  progress: number;
  uploadedSize: number;
  deleting: boolean;
};

type State = {
  uploadingFiles: FileInfo[];
  files: FileInfo[];
  error: string;
};

const toKB = (bytes: number) => roundNumber(bytes / 1024, 1);
const toMB = (bytes: number) => roundNumber(toKB(bytes) / 1024, 1);
const DEFAULT_MAX_FILE_SIZE_MB = 5;
const EXISTING_FILE_SIZE = 0;

export class Upload extends Component<Props, State> {
  readonly uppy: Uppy<Meta, UploadResponse>;
  errorTimeout?: ReturnType<typeof setTimeout>;

  constructor(props: Props) {
    super(props);
    this.state = {
      uploadingFiles: [],
      files: props.uploadedFileName
        ? (Array.isArray(props.uploadedFileName) ? props.uploadedFileName : [props.uploadedFileName]).map((fileName) =>
            this.createExistingFileInfo(fileName),
          )
        : [],
      error: '',
    };

    const maxFileSizeMb = this.props.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
    const maxFileSize = 1024 * 1024 * maxFileSizeMb;

    this.uppy = new Uppy<Meta, UploadResponse>({
      autoProceed: true,
      restrictions: {
        maxFileSize,
        minNumberOfFiles: 1,
        allowedFileTypes: this.props.fileTypes,
        ...(this.props.multiple ? {} : { maxNumberOfFiles: 1 }),
      },
    })
      .use(XHRUpload, {
        endpoint: this.props.url,
        limit: 3,
        method: 'POST',
        formData: true,
        fieldName: 'file',
      })
      .on('file-added', (file) => {
        const fileSizeConverter = this.props.sizeView === 'kb' ? toKB : toMB;

        const fileInfo: FileInfo = {
          id: file.id,
          fileId: null,
          name: file.name ?? file.meta.name,
          displayName: file.name ?? file.meta.name,
          size: fileSizeConverter(file.size ?? 0),
          progress: 0,
          uploadedSize: 0,
          deleting: false,
        };

        this.clearError();
        this.setState((state) => ({ uploadingFiles: [...state.uploadingFiles, fileInfo] }));
      })
      .on('restriction-failed', (_file, error) => {
        this.showError(error.message || 'Файл не прошел проверку');
      })
      .on('upload-progress', (file, progress) => {
        const fileSizeConverter = this.props.sizeView === 'kb' ? toKB : toMB;

        this.setState((state) => ({
          uploadingFiles: state.uploadingFiles.map((item) =>
            item.id === file?.id
              ? {
                  ...item,
                  progress: progress.bytesTotal
                    ? roundNumber((progress.bytesUploaded / progress.bytesTotal) * 100, 0)
                    : 0,
                  uploadedSize: fileSizeConverter(progress.bytesUploaded),
                }
              : item,
          ),
        }));
      })
      .on('upload-success', (file, response) => {
        if (!file) return;

        if (response.body && response.body.success) {
          const { result } = response.body;

          this.setState(
            (state) => {
              const uploadedFile = state.uploadingFiles.find((item) => item.id === file.id);
              if (!uploadedFile) return null;

              return {
                files: [
                  ...state.files,
                  {
                    ...uploadedFile,
                    fileId: result.id ?? null,
                    name: result.name,
                    displayName: result.name,
                  },
                ],
                uploadingFiles: state.uploadingFiles.filter((item) => item.id !== file.id),
              };
            },
            () => {
              if (this.state.files.some((item) => item.id === file.id && item.name === result.name)) {
                this.props.onUploaded(this.props.fieldName, this.getUploadedFileValue(this.state.files));
              }
            },
          );
        } else if (response.body && !response.body.success) {
          const { errorMessage } = response.body;
          this.setState((state) => ({
            error: errorMessage,
            uploadingFiles: state.uploadingFiles.filter((item) => item.id !== file.id),
          }));
          this.removeUppyFile(file.id);
          this.showError(errorMessage);
        }
      })
      .on('upload-error', (file) => {
        this.removeUploadingFile(file?.id, 'Произошла непредвиденная ошибка');
      });
  }

  get fileSizeMeasure() {
    return this.props.sizeView === 'kb' ? 'Кб' : 'Мб';
  }

  createExistingFileInfo = (fileName: string): FileInfo => ({
    fileId: null,
    name: fileName,
    displayName: fileName,
    size: EXISTING_FILE_SIZE,
    progress: 100,
    uploadedSize: EXISTING_FILE_SIZE,
    deleting: false,
  });

  getUploadedFileValue = (files: FileInfo[]): string | string[] => {
    const fileNames = files.map((item) => item.name);

    return this.props.multiple ? fileNames : fileNames[0] || '';
  };

  clearError = () => {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = undefined;
    }

    if (this.state.error) {
      this.setState({ error: '' });
    }
  };

  showError = (error: string) => {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }

    this.setState({ error });
    this.errorTimeout = setTimeout(() => this.setState({ error: '' }), 5000);
  };

  removeUppyFile = (fileId?: string) => {
    if (fileId) {
      this.uppy.removeFile(fileId);
    }
  };

  removeUploadingFile = (fileId?: string, error?: string) => {
    this.setState((state) => ({
      uploadingFiles: state.uploadingFiles.filter((item) => item.id !== fileId),
    }));
    this.removeUppyFile(fileId);

    if (error) {
      this.showError(error);
    }
  };

  componentWillUnmount() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
    this.uppy.destroy();
  }

  deleteFile = (file: FileInfo) => {
    const { files } = this.state;

    files.forEach((item) => {
      if (item.id === file.id) {
        item.deleting = true;
      }
    });

    this.setState({ files });
    this.removeFile(file);
    //
    // this.props.onRemove({ id: file.fileId }).then(result => {
    //   const { success } = result;
    //
    //   if (success) {
    //     this.removeFile(file);
    //   }
    // });
  };

  cancelUpload = (file: FileInfo) => {
    this.setState((state) => ({
      uploadingFiles: state.uploadingFiles.filter((item) => item.id !== file.id),
    }));
    this.removeUppyFile(file.id);
  };

  removeFile = (file: FileInfo) => {
    const { files } = this.state;

    const updatedFiles = files.filter((item) => item.id !== file.id && item.name !== file.name);
    const uploadedFileNames = this.getUploadedFileValue(updatedFiles);

    this.props.onRemove(file.name);
    this.props.onUploaded(this.props.fieldName, uploadedFileNames);

    this.clearError();
    this.setState({ files: updatedFiles });
    this.removeUppyFile(file.id);
  };

  render() {
    const { files, uploadingFiles, error } = this.state;

    return (
      <div className="upload-form">
        <div
          className={cn('form-upload', {
            'form-upload-disabled': this.props.disabled,
          })}
          style={this.props.disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
        >
          <DragDrop
            uppy={this.uppy}
            locale={{
              strings: {
                dropHereOr: this.props.dropHereOr || 'Прикрепить файл',
              },
            }}
          />
        </div>
        {uploadingFiles &&
          uploadingFiles.map((item, index) => (
            <div key={`${item.id || item.name}-${index}`} className="upload-row">
              <div className="upload-row__header">
                <span className="upload-row__header-name">{item.name}</span>
                <span className="upload-row__header-cancel" onClick={() => this.cancelUpload(item)}>
                  Отменить загрузку
                </span>
              </div>
              <div className="upload-row__progress-bar-wrapper">
                <div className="upload-row__progress-bar" style={{ width: `${item.progress}%` }} />
              </div>
              <div className="upload-row__footer">
                <span className="upload-row__footer-progress">{item.progress}%</span>
                <span className="upload-row__footer-size">
                  {item.uploadedSize}
                  {this.fileSizeMeasure} из {item.size}
                  {this.fileSizeMeasure}
                </span>
              </div>
            </div>
          ))}

        {error && <p className="error-message">{error}</p>}

        <div className="form-control-row__separator" />

        {files &&
          files.map((item, index) => (
            <div
              key={`${item.id || item.name}-${index}`}
              className={cn('uploaded-row', {
                'uploaded-row-deleting': item.deleting,
              })}
            >
              <FileIcon className="uploaded-row__file" />
              <span className="uploaded-row__name">{item.displayName}</span>
              {item.size > 0 && (
                <span className="uploaded-row__size">
                  {item.size}
                  {this.fileSizeMeasure}
                </span>
              )}
              <DeleteIcon className="uploaded-row__delete" onClick={() => this.deleteFile(item)} />
            </div>
          ))}
      </div>
    );
  }
}
